import moment from "moment";
import { getWeekOfMonth } from "date-fns";
import { weekOfMonth } from "../../src/utils/Constants";

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

//Returns a list of all booking date/time information for all bookings that
//need to be made according to the initialDate, untilDate and ruleComponents
//For the Reschedule scenerio, we only need to consider the number of
async function prepareBookingsDateTimeListForReschedule(
    apptsByOrder,
    originalBookingData,
    initialDateTime,
    originalRruleUntilDate,
    untilDate,
    ruleComponents,
    rruleChanged,
    xoccurChanged
) {
    const bookings = [];
    const [initialDate, initialFullTime] = initialDateTime.split("T");
    const [initialTime, timezoneOffset] = initialFullTime.split("-");
    let currentDate = initialDate;
    if (
        ruleComponents.frequency === "MONTHLY" &&
        ruleComponents.days === "WEEK" &&
        isLastOccurence(currentDate)
    ) {
        ruleComponents.lastOccurence = true;
    }

    if (!xoccurChanged) {
        ruleComponents.xoccur = apptsByOrder.length;
    }

    let offsetUntilDate = await getCheckUntilDate(
        initialDateTime,
        ruleComponents
    );

    if (rruleChanged && originalRruleUntilDate !== untilDate) {
        offsetUntilDate =
            untilDate < offsetUntilDate ? untilDate : offsetUntilDate;
    }

    //Prepares the 'bookings' array with a 'bookingObject's for each booking that will take place from the currentDate to the untilDate
    while (currentDate <= offsetUntilDate) {
        let currDate = new Date(`${currentDate}T${initialTime}`);

        const bookingObject = {
            date: currDate,
            dt_disp: moment(currDate).format("YYYY-MM-DD")
        };

        bookings.push(bookingObject);

        //If there is a set number of x occurences, break from the while loop and return the bookings once the x number of occurences is reached
        if (ruleComponents.xoccur && bookings.length == ruleComponents.xoccur) {
            return bookings;
        }

        // Update currentDate for the next iteration based on the rule components
        currentDate = calculateNextDate(currentDate, ruleComponents);
    }

    return bookings;
}

//Parses the rrule and returns the rrule has an object with its respective attributes
function parseRRule(rrule) {
    const components = rrule.split(";");
    const parsedRule = {
        frequency: "",
        interval: 1,
        days: [],
        until: null,
        xoccur: null
    };

    components.forEach((component) => {
        const [key, value] = component.split("=");
        switch (key) {
            case "FREQ":
                parsedRule.frequency = value;
                break;
            case "INTERVAL":
                parsedRule.interval = parseInt(value, 10);
                break;
            case "BYDAY":
                if (value) {
                    if (value === "NUMBER" || value === "WEEK") {
                        parsedRule.days = value;
                        break;
                    }
                    const daysArray = value.split(",");
                    daysArray.forEach((day) => {
                        parsedRule.days.push(convertDayToNumber(day));
                    });
                    //sort parsedRule.days in ascending order
                    parsedRule.days.sort((a, b) => a - b);
                }
                break;
            case "UNTIL":
                parsedRule.until = value;
                break;
            case "XOCCUR":
                parsedRule.xoccur = parseInt(value);
                break;
            default:
                // Handle other RRULE components if necessary
                break;
        }
    });

    return parsedRule;
}

/**
 * Determines the future date ('until' date) that we generate and check valid bookings for in the front end
 * We need to because we cannot check the validity for every booking that is part of a recurring booking set
 * Instead, we check a specifed distance in the future to check until
 * @param {Date} startDate - The start date
 * @param {Object} ruleComponents - The components of the rrule
 * @returns {string} - The cutoff date until which valid bookings will be considered, YYYY-MM-DD format.
 */
async function getCheckUntilDate(startDate, ruleComponents) {
    // Create a moment object based on the start date
    let untilDate = moment(startDate);

    /* console.log(
        "omar this is the startDate and ruleComponents",
        startDate,
        ruleComponents,
        untilDate.format("YYYY-MM-DD")
    ); */

    if (ruleComponents.frequency === "WEEKLY") {
        // Add 2 months to the moment object
        untilDate.add(2, "months");

        return untilDate.format("YYYY-MM-DD");
    } else if (ruleComponents.frequency === "MONTHLY") {
        // Add 6 months to the moment object
        untilDate.add(6, "months");

        return untilDate.format("YYYY-MM-DD");
    } else if (ruleComponents.frequency === "YEARLY") {
        // Add 2 months to the moment object
        untilDate.add(2, "months");

        return untilDate.format("YYYY-MM-DD");
    }
}

function convertDayToNumber(day) {
    const dayMappings = {
        MO: 0,
        TU: 1,
        WE: 2,
        TH: 3,
        FR: 4,
        SA: 5,
        SU: 6
    };
    return dayMappings[day];
}

//returns true , if the date is the 5th and last occurence in a month
export function isLastOccurence(dateInput) {
    const date = new Date(dateInput);
    const currentOccurrence = Math.floor((date.getDate() - 1) / 7) + 1;
    return currentOccurrence === 5;
}
function calculateNextDate(currentDate, ruleComponents) {
    const { frequency, interval, days, lastOccurence } = ruleComponents;
    let nextDate = moment(currentDate);

    // Get the day of the week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
    // Then convert it to MB calendar week setting (0 = Monday, 1 = Tuesday, ..., 6 = Sunday)
    let currentDay = nextDate.day();
    currentDay = (currentDay + 6) % 7;

    if (frequency === "WEEKLY") {
        let daysToAdd;
        if (days.length > 1) {
            let closestValidDay = days.find((day) => day > currentDay);
            if (!closestValidDay) {
                closestValidDay = days[0];
                daysToAdd =
                    7 - (currentDay - closestValidDay) + (interval - 1) * 7;
            } else {
                daysToAdd = closestValidDay - currentDay;
            }
        } else {
            daysToAdd = 7 * interval;
        }

        nextDate.add(daysToAdd, "days");
    } else if (frequency === "MONTHLY") {
        if (days === "WEEK") {
            const currentOccurrence = Math.floor((nextDate.date() - 1) / 7) + 1;

            nextDate.add(interval, "months");

            if (lastOccurence) {
                nextDate.add(1, "months").date(0);

                while ((nextDate.day() + 6) % 7 !== currentDay) {
                    nextDate.subtract(1, "days");
                }
                return nextDate.format("YYYY-MM-DD");
            } else if (currentOccurrence > 1) {
                nextDate.date(7 * (currentOccurrence - 1));
            } else {
                nextDate.date(1);
            }

            while (
                (nextDate.day() + 6) % 7 !== currentDay ||
                Math.floor((nextDate.date() - 1) / 7) + 1 !== currentOccurrence
            ) {
                nextDate.add(1, "days");
            }
        } else if (days === "NUMBER") {
            let tempNextDate;
            const dayOfMonth = nextDate.date();
            let nextMonthDay;
            let count = 1;

            while (dayOfMonth !== nextMonthDay) {
                tempNextDate = nextDate.clone().add(interval * count, "months");
                nextMonthDay = tempNextDate.date();

                if (dayOfMonth === nextMonthDay) {
                    nextDate = tempNextDate;
                    break;
                }
                count++;
            }
        }
    } else if (frequency === "YEARLY") {
        nextDate.add(interval, "years");
    }
    return nextDate.format("YYYY-MM-DD");
}

const generateRRULE = (
    chronologicalSelector,
    numberSelector,
    selectedDays,
    endDate,
    xoccur
) => {
    const interval = numberSelector; // Set the interval based on numberSelector
    const dayAbbreviations = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];
    let days;

    if (chronologicalSelector === "weeks") {
        // Map the selected day numbers to day abbreviations
        const selectedDayAbbreviations = selectedDays
            .sort((a, b) => a - b)
            .map((dayNumber) => dayAbbreviations[dayNumber]);
        days = selectedDayAbbreviations.join(",");
    } else if (chronologicalSelector === "months") {
        days = selectedDays;
    } else if (chronologicalSelector === "years") {
        days = selectedDays;
    }

    let formattedDate;
    if (endDate) {
        // Parse the date using Moment.js
        let dateObject = moment(endDate);

        // Extract components
        let year = dateObject.format("YYYY");
        let month = dateObject.format("MM");
        let day = dateObject.format("DD");

        // Create "YYYY-MM-DD" format
        formattedDate = `${year}-${month}-${day}`;
    }
    // Set the until date, and occurrences
    const endDateStr = xoccur
        ? `;UNTIL=2099-12-31;XOCCUR=${xoccur}`
        : endDate
          ? `;UNTIL=${formattedDate}`
          : "";

    // Set the frequency based on chronologicalSelector
    const freq =
        chronologicalSelector === "weeks"
            ? "WEEKLY"
            : chronologicalSelector === "months"
              ? "MONTHLY"
              : "YEARLY";

    return `FREQ=${freq};INTERVAL=${interval};BYDAY=${days}${endDateStr}`;
};

function daysArrayToString(daysArray) {
    //adjust so monday is start of week (0 = Monday, 6 = Sunday)
    const adjustedDaysArray = daysArray.map((day) => (day === 0 ? 6 : day - 1));

    // Sort the array to ensure it is in the correct order
    adjustedDaysArray.sort((a, b) => a - b);

    let result = [];
    let start = null;
    let end = null;

    for (let i = 0; i < adjustedDaysArray.length; i++) {
        if (start === null) {
            start = adjustedDaysArray[i];
        }

        if (end === null || adjustedDaysArray[i] === end + 1) {
            end = adjustedDaysArray[i];
        } else {
            if (start === end) {
                result.push(DAY_NAMES[start]);
            } else {
                result.push(`${DAY_NAMES[start]} \u2014 ${DAY_NAMES[end]}`);
            }
            start = adjustedDaysArray[i];
            end = adjustedDaysArray[i];
        }
    }

    // Handle the last range or single day
    if (start !== null) {
        if (start === end) {
            result.push(DAY_NAMES[start]);
        } else {
            result.push(`${DAY_NAMES[start]} \u2014 ${DAY_NAMES[end]}`);
        }
    }

    return result.join(", ");
}

/**
 * Retrieves the first rrule from the rrule list that pertains to the given selected date.
 *
 * @param {string} selectedDate - The selected date in 'YYYY-MM-DD' format.
 * @param {Array<string>} rruleList - List of rrules in string format.
 * @returns {string|null} The first relevant rrule, or null if none match the criteria.
 */
function getRelevantRRule(selectedDate, rruleList) {
    for (const rrule of rruleList) {
        const untilMatch = /UNTIL=([0-9-]+)|$/.exec(rrule);
        const untilDate = untilMatch ? untilMatch[1] : null;

        if (!untilDate || untilDate >= selectedDate) {
            return rrule;
        }
    }

    // Return null if no matching rule is found
    return null;
}

function parseRRuleForSelector(rrule) {
    const components = rrule.split(";");
    const parsedRule = {
        frequency: "",
        interval: 1,
        days: [],
        until: null,
        xoccur: null
    };

    components.forEach((component) => {
        const [key, value] = component.split("=");
        switch (key) {
            case "FREQ":
                if (value === "WEEKLY") {
                    parsedRule.frequency = "weeks";
                } else if (value === "MONTHLY") {
                    parsedRule.frequency = "months";
                } else if (value === "YEARLY") {
                    parsedRule.frequency = "years";
                }
                break;
            case "INTERVAL":
                parsedRule.interval = parseInt(value, 10);
                break;
            case "BYDAY":
                if (value) {
                    if (value === "NUMBER" || value === "WEEK") {
                        parsedRule.days = value;
                        break;
                    }
                    const daysArray = value.split(",");
                    daysArray.forEach((day) => {
                        parsedRule.days.push(convertDayToNumber(day));
                    });
                    //sort parsedRule.days in ascending order
                    parsedRule.days.sort((a, b) => a - b);
                }
                break;
            case "UNTIL":
                parsedRule.until = value;
                break;
            case "XOCCUR":
                parsedRule.xoccur = value;
                break;
            default:
                break;
        }
    });

    return parsedRule;
}

export function getFullDayString(day) {
    const dayMap = {
        MO: "Mon",
        TU: "Tue",
        WE: "Wed",
        TH: "Thu",
        FR: "Fri",
        SA: "Sat",
        SU: "Sun"
    };

    return dayMap[day] || day; // If the day is not found in the map, return the original value
}
export function formatByDay(byDay) {
    const days = byDay.split(",");
    const formattedDays = days.map(getFullDayString);

    if (formattedDays.length <= 1) {
        return formattedDays.join("");
    }

    // Join all but the last day with commas
    const allButLast = formattedDays.slice(0, -1).join(", ");

    // Join the last day with "and"
    const lastDay = formattedDays[formattedDays.length - 1];

    return `${allButLast} and ${lastDay}`;
}

export function parseRruleString(rruleString) {
    const rruleObject = {};
    const parts = rruleString.split(";");
    parts.forEach((part) => {
        const [key, value] = part.split("=");
        rruleObject[key] = value;
    });
    return rruleObject;
}

export const constructRruleLabel = (rruleString, bookingDate, startDate) => {
    try {
        // Parse the rruleString into an object
        const rruleObject = parseRruleString(rruleString);

        // Extract relevant information
        const freq = rruleObject.FREQ || "";
        const interval = rruleObject.INTERVAL || 1;
        const byDay = rruleObject.BYDAY || "";
        let until = rruleObject.UNTIL || "";
        const xOccur = rruleObject.XOCCUR || "";

        if (startDate) {
            const inputDate = new Date(startDate);
            const options = {
                month: "2-digit",
                day: "2-digit",
                year: "numeric"
            };
            const startingDate = inputDate.toLocaleDateString("en-US", options);
            if (until) {
                until = new Date(`${until}T00:00:00`).toLocaleDateString(
                    "en-US",
                    options
                );
            }
        }

        let label = "";

        //FREQ/INTERVAL
        if (freq === "WEEKLY") {
            if (interval > 1) {
                label += `Every ${interval} weeks`;
            } else {
                label += `Weekly`;
            }
        } else if (freq === "MONTHLY") {
            if (interval > 1) {
                label += `Every ${interval} months`;
            } else {
                label += `Monthly`;
            }
        } else if (freq === "YEARLY") {
            if (interval > 1) {
                label += `Every ${interval} years`;
            } else {
                label += `Yearly`;
            }
        }

        //BYDAY
        if (byDay && freq === "WEEKLY") {
            label += ` on ${formatByDay(byDay)}`;
        } else if (byDay === "NUMBER" && freq === "MONTHLY") {
            const date = bookingDate
                ? new Date(bookingDate)
                : new Date(startDate);
            const dayOfMonth = date.getDate();
            label += ` on day ${dayOfMonth}`;
        } else if (byDay === "WEEK" && freq === "MONTHLY") {
            const date = bookingDate
                ? new Date(bookingDate)
                : new Date(startDate);
            const dayOfWeek = date.toLocaleDateString("en-US", {
                weekday: "long"
            });
            const weekOccurence = getWeekOfMonth(date.getDate());

            label += ` on the ${weekOfMonth[weekOccurence - 1]} ${dayOfWeek}`;
        } else if (freq === "YEARLY") {
            label += `On ${moment(bookingDate).format("MMMM Do")}`;
        }
        if (startDate) {
            label += ` starting ${startDate}`;
        }
        //XOCCUR/UNTILL
        /* if (xOccur && rruleChanged && !xoccurChanged) {
        label += `, for all remaining sessions`;
    } else */ if (xOccur) {
            label += `, for a total of ${xOccur} sessions`;
        } else if (until) {
            label += `, until ${getUntilDateDisplay(until)}`;
        }

        //setRruleLabel(label);
        return label;
    } catch (e) {
        console.log("error constructing rrule label", e);
    }
};

function getUntilDateDisplay(untilDate) {
    if (untilDate instanceof Date) {
        return untilDate.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric"
        });
    } else
        return new Date(`${untilDate}T00:00:00`).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric"
        });
}

/**
 * Converts an rruleString into a user-friendly sentence for easy comprehension.
 *
 * @param {string} rruleString - The input rruleString to be converted, e.g., "FREQ=MONTHLY;INTERVAL=1;BYDAY=WEEK;UNTIL=2024-03-31".
 * @param {string} [bookingDate] - Optional booking date in the format "YYYY-MM-DD".
 *
 * @returns {string} A user-friendly sentence describing the recurrence rule.
 *
 */
export function getWdDisplay(rruleString, bookingDate) {
    // Parse the rruleString into an object
    const rruleObject = parseRruleString(rruleString);

    // Extract relevant information
    const freq = rruleObject.FREQ || "";
    const interval = rruleObject.INTERVAL || 1;
    const byDay = rruleObject.BYDAY || "";

    //until = new Date(until);
    // Build the user-friendly label
    let label = ``;
    //BYDAY
    if (byDay && freq === "WEEKLY") {
        label += ` ${formatByDay(byDay)}, every ${interval} week${
            interval > 1 ? `s` : ""
        }`;
    } else if (byDay === "NUMBER" && freq === "MONTHLY") {
        label += ` ${interval} month${
            interval > 1 ? `s` : ""
        }, on the same day`;
    } else if (byDay === "WEEK" && freq === "MONTHLY") {
        const date = new Date(bookingDate);

        const dayOfWeek = date.toLocaleDateString("en-US", {
            weekday: "long"
        });
        const weekOccurence = Math.floor((date.getDate() - 1) / 7) + 1;
        const occurenceLabels = ["first", "second", "third", "fourth", "last"];

        label += ` ${
            occurenceLabels[weekOccurence - 1]
        } ${dayOfWeek} of every ${interval} month${interval > 1 ? `s` : ""}`;
    } else if (freq === "YEARLY") {
        label += ` ${interval} year${interval > 1 ? `s` : ""} on the same day`;
    }

    return label;
}

/**
 * Converts RFC5545 formatted RRULE to malformed MB rrule format.
 *
 * @param {String} rfcRRuleStr The RFC5545 formatted RRULE string (e.g., "FREQ=DAILY;INTERVAL=1;BYDAY=MO,TU;COUNT=10").
 * @returns {String} The malformed MB rrule string (e.g., "FREQ=DAILY;INTERVAL=1;BYDAY=MO,TU;XOCCUR=10").
 */
function convertRRuleFromRFC5545(rfcRRuleStr) {
    let rruleMap = {};

    // Parse the RFC RRULE string into key-value pairs
    rfcRRuleStr.split(";").forEach((part) => {
        const [key, value] = part.split("=");
        rruleMap[key] = value;
    });

    //Convert COUNT to XOCCUR
    if (rruleMap.COUNT) {
        rruleMap.XOCCUR = rruleMap.COUNT;
        delete rruleMap.COUNT;
    }

    //Handle UNTIL format conversion: 20250331T235959Z -> 2025-03-31
    if (rruleMap.UNTIL) {
        const untilDatePart = rruleMap.UNTIL.substring(0, 8);
        const year = untilDatePart.substring(0, 4);
        const month = untilDatePart.substring(4, 6);
        const day = untilDatePart.substring(6, 8);
        rruleMap.UNTIL = `${year}-${month}-${day}`;
    } else if (rruleMap.XOCCUR) {
        rruleMap.UNTIL = "2099-12-31";
    }

    //Handle Monthly RRULE with BYDAY (inverse of BYDAY=WEEK transformation)
    //If FREQ is MONTHLY and BYDAY matches the pattern (e.g., "1MO") convert it back to "WEEK".
    if (
        rruleMap.FREQ === "MONTHLY" &&
        rruleMap.BYDAY &&
        /^\d{1,2}[A-Z]{2}$/.test(rruleMap.BYDAY)
    ) {
        rruleMap.BYDAY = "WEEK";
    }

    //Handle Monthly RRULE with BYMONTHDAY
    if (
        rruleMap.FREQ === "MONTHLY" &&
        rruleMap.BYMONTHDAY &&
        rruleMap.BYDAY !== "WEEK"
    ) {
        rruleMap.BYDAY = "NUMBER";
        delete rruleMap.BYMONTHDAY;
    }

    //Handle Yearly RRULE
    if (rruleMap.FREQ === "YEARLY") {
        delete rruleMap.BYMONTH;
        delete rruleMap.BYMONTHDAY;
    }

    //Reconstruct the malformed RRULE string from the modified map
    let malformedRRule = Object.entries(rruleMap)
        .map(([key, value]) => `${key}=${value}`)
        .join(";");

    return malformedRRule;
}

export const FREQ_MONTHLY = "MONTHLY";
export const FREQ_YEARLY = "YEARLY";
export const FREQ_WEEKLY = "WEEKLY";
export {
    prepareBookingsDateTimeListForReschedule,
    calculateNextDate,
    parseRRule,
    generateRRULE,
    daysArrayToString,
    parseRRuleForSelector,
    getRelevantRRule,
    convertRRuleFromRFC5545
};
